import { useState, useRef, useEffect, useCallback, type CSSProperties } from 'react';

// ErrorCard — Polish B2 reliability tranche. Uniform failure-recovery surface for long-call errors (quality
// read, tag inference, guided classify, import parse). Ported from PSCraft v2's workspace/ErrorCard.tsx
// pattern into CVCraft's inline-style idiom (no tailwind here).
//
// Contract: Retry renders ONLY when the caller passes retrySafe === true — which the caller derives from the
// server's error envelope (`{error, user_message, category, retry_safe}`, workers/src/lib/userErrors.ts). This
// component never invents that field. While `onRetry`'s promise is in flight the button is disabled + aria-busy
// + labeled "Retrying…"; after it resolves a 1s cooldown keeps it disabled ("Just a moment…") before re-enabling —
// so a paid API call can't be click-mashed. Non-retryable errors show guidance text instead of a dead button.

const RETRY_COOLDOWN_MS = 1000;

export function ErrorCard({
  title, message, retrySafe, onRetry, guidance, testId = 'error-card',
}: {
  title: string;
  message: string;
  retrySafe: boolean;
  onRetry?: () => Promise<void>;
  // Shown instead of/alongside a Retry button when the error is not retry-safe — honest "what to do instead".
  guidance?: string;
  testId?: string;
}) {
  const [phase, setPhase] = useState<'idle' | 'retrying' | 'cooldown'>('idle');
  const cooldownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (cooldownTimer.current) clearTimeout(cooldownTimer.current); }, []);

  const handleRetry = useCallback(async () => {
    if (phase !== 'idle' || !onRetry) return;
    setPhase('retrying');
    try {
      await onRetry();
    } finally {
      setPhase('cooldown');
      cooldownTimer.current = setTimeout(() => setPhase('idle'), RETRY_COOLDOWN_MS);
    }
  }, [phase, onRetry]);

  const retryLabel = phase === 'retrying' ? 'Retrying…' : phase === 'cooldown' ? 'Just a moment…' : 'Retry';

  return (
    <div style={S.card} role="region" aria-label={title} data-testid={testId}>
      <p style={S.title} data-testid={`${testId}-title`}>{title}</p>
      <p style={S.message} data-testid={`${testId}-message`}>{message}</p>
      {retrySafe && onRetry && (
        <button
          type="button"
          className="btn btn-primary"
          style={S.retry}
          onClick={() => void handleRetry()}
          disabled={phase !== 'idle'}
          aria-busy={phase === 'retrying'}
          data-testid={`${testId}-retry`}
        >
          {retryLabel}
        </button>
      )}
      {!retrySafe && guidance && <p style={S.guidance} data-testid={`${testId}-guidance`}>{guidance}</p>}
    </div>
  );
}

const S: Record<string, CSSProperties> = {
  card: { background: 'var(--card)', border: '1px solid var(--rule)', borderRadius: 8, padding: 'var(--space-4) var(--space-4)', margin: 'var(--space-3) 0', textAlign: 'center', fontFamily: 'var(--sans)' },
  title: { margin: '0 0 var(--space-2)', fontFamily: 'var(--serif)', fontSize: 'var(--fs-h3)', color: 'var(--ink)' },
  message: { margin: '0 0 var(--space-3)', fontSize: 'var(--fs-small)', color: 'var(--ink-subtle)', lineHeight: 'var(--lh-body)' },
  retry: { minHeight: 44, minWidth: 120, fontSize: 'var(--fs-small)' },
  guidance: { margin: 0, fontSize: 'var(--fs-small)', color: 'var(--muted)' },
};
